import { type SectionBackdropProps } from '@/components/atoms';
import { type CompanyStoryBandOptions, useCompanyStoryBand } from '@/hooks/useCompanyStoryBand';
import { type SectionPatternName } from '@/theme';

import { Section, type SectionTone } from '../Section';

import { CompanyStoryCompact } from './CompanyStoryCompact';
import { CompanyStoryFull } from './CompanyStoryFull';

export type CompanyStoryBandProps = CompanyStoryBandOptions & {
  tone?: SectionTone;
  pattern?: SectionPatternName;
  backdrop?: SectionBackdropProps;
};

/**
 * Who Kabex Motors is, as a band any page can render.
 *
 * ## Why it is shared
 *
 * It was `pages/about/organisms/StoryBand` — the About page's opening, and the
 * only place on the site that said how long the company had been doing this or
 * where to find it. The page it lived on is reachable from a "Company"
 * dropdown, which is where visitors were not finding it, so the band is now
 * lent to five of the pages they do reach (home, /how-it-works, /reviews,
 * /import-a-car, /services) in a shorter form that links back.
 *
 * That makes it the third band on this pattern, after `DeliveryProofBand` and
 * `ImportPromiseBand`, and it takes the same props they do — `tone`,
 * `pattern`, `backdrop` and an overridable heading — because a band appearing
 * on six pages has to be able to sit correctly in six different places.
 *
 * ## What it decides
 *
 * The layout, and nothing else. Which words, which figures, which link and
 * which narrative length are all resolved by `useCompanyStoryBand`; this
 * component reads one boolean off the result and picks a tree, the way
 * `PageIntro` does. The two layouts are answerable on their own, and neither
 * holds a fact.
 *
 * ## Tone is the host page's call, not this band's
 *
 * There is no default tone here on purpose. The site has two light tones and
 * every one of these pages already alternates between them, so the only tone
 * that reads as a boundary is the one its particular neighbours are not
 * wearing — which this component cannot know. Each page states it at the call
 * site, with the reasoning for that page beside it.
 */
export function CompanyStoryBand({
  tone,
  pattern,
  backdrop,
  ...options
}: CompanyStoryBandProps = {}) {
  const model = useCompanyStoryBand(options);

  return (
    // Spread rather than passed: `exactOptionalPropertyTypes` is on, so
    // handing `Section` an explicit `undefined` is not the same as omitting
    // the prop, and a band that was given no tone has to omit it so the
    // Section's own default applies.
    <Section
      labelledBy={model.titleId}
      {...(tone ? { tone } : {})}
      {...(pattern ? { pattern } : {})}
      {...(backdrop ? { backdrop } : {})}
    >
      {model.isCompact ? <CompanyStoryCompact model={model} /> : <CompanyStoryFull model={model} />}
    </Section>
  );
}
